import type { BeliefNode, BeliefTier } from '../types';

const DECAY_RATES: Record<BeliefTier, number> = {
  axiomatic: 0,
  active: 0.05,
  situational: 0.15,
};

const CONFIDENCE_FLOORS: Record<BeliefTier, number> = {
  axiomatic: 0.5,
  active: 0,
  situational: 0,
};

const MS_PER_MONTH = 30 * 24 * 60 * 60 * 1000;
const SITUATIONAL_ARCHIVE_MS = 60 * 24 * 60 * 60 * 1000; // 60 days
const ACTIVE_ARCHIVE_MONTHS = 3;

export function computeEffectiveConfidence(node: BeliefNode, now: Date = new Date()): number {
  const tier = node.tier || 'active';
  const floor = CONFIDENCE_FLOORS[tier];
  const rate = DECAY_RATES[tier];

  if (rate === 0) return Math.max(floor, node.confidence);

  const lastMs = typeof node.lastReinforced?.toMillis === 'function'
    ? node.lastReinforced.toMillis()
    : (node.lastReinforced as unknown as { seconds: number })?.seconds * 1000 || now.getTime();

  const monthsUnreinforced = (now.getTime() - lastMs) / MS_PER_MONTH;
  const decayed = node.confidence - (monthsUnreinforced * rate);

  return Math.max(floor, decayed);
}

export function shouldArchive(node: BeliefNode, now: Date = new Date()): boolean {
  if (node.status === 'archived') return false;
  const tier = node.tier || 'active';
  if (tier === 'axiomatic') return false;

  const lastMs = typeof node.lastReinforced?.toMillis === 'function'
    ? node.lastReinforced.toMillis()
    : (node.lastReinforced as unknown as { seconds: number })?.seconds * 1000 || now.getTime();

  const elapsed = now.getTime() - lastMs;

  if (tier === 'situational') {
    return elapsed > SITUATIONAL_ARCHIVE_MS;
  }

  if (tier === 'active') {
    const monthsUnreinforced = elapsed / MS_PER_MONTH;
    return monthsUnreinforced >= ACTIVE_ARCHIVE_MONTHS && computeEffectiveConfidence(node, now) <= 0.2;
  }

  return false;
}
